import { Badge } from "@/components/ui/badge";
import { faqNeedsRefinement, type Faq } from "@/features/faq/types";
import { useDateFormat, useTranslation } from "@/features/utils/i18n";
import { WarningCircleIcon } from "@phosphor-icons/react";
import type { ColumnDef } from "@tanstack/react-table";
import { Action } from "./columns-action";

export function useFaqColumns(categories: string[] = []): ColumnDef<Faq>[] {
  const { t } = useTranslation("faq");
  const { format } = useDateFormat();

  return [
    {
      accessorKey: "question",
      header: t("table.question"),
      cell: ({ row }) => {
        const faq = row.original;
        const answer = faq.refined_response || faq.ai_response;
        return (
          <div className="max-w-md space-y-1">
            <div className="flex items-center gap-2 font-medium">
              <span className="truncate">{faq.question}</span>
              {faq.kind === "situation" && (
                <Badge variant="secondary" className="shrink-0">
                  {t("table.situation")}
                </Badge>
              )}
              {faqNeedsRefinement(faq) && (
                <Badge
                  variant="outline"
                  className="shrink-0 border-amber-500/40 text-amber-600"
                >
                  <WarningCircleIcon className="mr-1 h-3 w-3" weight="fill" />
                  {t("table.needsRefinement")}
                </Badge>
              )}
            </div>
            {answer ? (
              <div className="text-muted-foreground line-clamp-2 text-sm">
                {answer}
              </div>
            ) : (
              <div className="text-muted-foreground text-sm italic">
                {t(
                  faq.kind === "situation"
                    ? "table.noProcedure"
                    : "table.noAnswer",
                )}
              </div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "category",
      header: t("table.category"),
      cell: ({ row }) =>
        row.original.category ? (
          <Badge variant="secondary">{row.original.category}</Badge>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
    {
      accessorKey: "ask_count",
      header: t("table.askCount"),
      cell: ({ row }) => (
        <span className="tabular-nums">{row.original.ask_count || 0}</span>
      ),
    },
    {
      accessorKey: "last_asked_at",
      header: t("table.lastAsked"),
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.last_asked_at
            ? format(row.original.last_asked_at, "Pp")
            : "-"}
        </span>
      ),
    },
    {
      id: "actions",
      header: () => <div className="text-right">{t("table.actions")}</div>,
      cell: ({ row }) => <Action faq={row.original} categories={categories} />,
    },
  ];
}
